import I from 'components/I'
import { differenceInMonths, isBefore } from 'date-fns'
import i18next from 'i18next'
import { mapValues } from 'lodash-es'
import { Trans } from 'react-i18next'
import { DistinguishableColors, newDate } from 'utils'
import FMT from 'utils/fmt'
import TimezonedModel from './TimezonedModel'

export default class Restaurant extends TimezonedModel {

	/** @typedef {'PICKUP'|'DELIVERY'|'DINEIN'} OrderType */
	/** @type string */ timezone_id
	/** @type string */ display_name
	/** @type int */ position
	/** @type boolean */ active
	/** @type boolean */ allow_tips
	/** Identifies if the restaurant is available for customers or not - know as "deactivated" in the Admin
	 * @see getEvenLockedRestaurants()
	 * @type boolean */ is_locked
	/** Hexadecimal color
	 * @see setRestaurants()
	 * @see Restaurant.defineColors()
	 * @type string */ color
	/** @type ?bool */ shipday_auto_dispatch_enabled
	/** List of keys that can be displayed with {@link FMT.orderType} or fed to other functions.
	 * @type string[] */ enabled_base_order_types
	/** Textual date/time for when each order type will stop being temporarily closed - this is mostly the earliest value between {@link closed_until_iso} and the nearest closure time for the restaurant. This value is calculated by the backend.
	 * @todo FIND A BETTER NAMING SCHEME FOR THESE PROPERTIES! Their naming is too similar, but they may have different values for the same order type, given here it's ALWAYS the final timestamp and on the texts, it also considers the restaurant open/close hours. This naming came from the backend and is probably "unchangeable" there, but we can at least reduce confusion on the Manager.
	 * @see closed_until_iso
	 * @type {{ DELIVERY:?string, DINEIN:?string, PICKUP:?string }} */ closed_until_texts
	/** ISO date strings for when the ordering type will reopen; unrelated to open/close hours of the restaurant!!
	 * @see closed_until_texts
	 * @internal
	 * @type {{ DELIVERY:?string, DINEIN:?string, PICKUP:?string }} */ closed_until_iso

	static get MAX_TIMEOUT_INTERVAL() { return Math.pow(2, 32) - 1 }

	/** @internal */
	static timeouts = {}

	constructor({ enabled_base_order_types = {}, delivery_ordering_closed_until, dinein_ordering_closed_until, pickup_ordering_closed_until, ...attrs }) {
		super(attrs)

		//the API doesn't send only the keys because it comes from a getter, which is used as a whole on the backend
		this.enabled_base_order_types = Object.keys(enabled_base_order_types).sort() //so all places have the same sorting

		this.closed_until_iso = {
			DELIVERY: delivery_ordering_closed_until,
			DINEIN  : dinein_ordering_closed_until,
			PICKUP  : pickup_ordering_closed_until
		}

		//for some reason - or most probably, none at all -, we use a space before the meridiem indicator since the start of the app - see FMT.date(). The backend seems to prefer no spaces, but to not change it everywhere and having to double check UI details, that space is removed when it comes from the API - e.g. Restaurant close times. That said, most style guides do use spaces, although they also prefer lowercase indicators: https://knowadays.com/blog/writing-the-time-am-and-pm-or-a-m-and-p-m/
		this.closed_until_texts = mapValues(this.closed_until_texts, t => t && t.replace(/(\d)(AM|PM)/i, '$1\u00A0$2')) //NBSP
	}

	get localTime() {
		return FMT.time(newDate(this.timezone_id))
	}

	/**
	 * @param form {'locationdetails'|'paymentoptions'|'locationhours'|'locationnotifications'|'ordertimes'|'delivery'|'locationuploads'|'locationlinks'}
	 * @returns {string}
	 */
	adminUrl(form = 'locationdetails') {
		return `${process.env.REACT_APP_BASE_URL}/admin/?form=${form}&id=${this.id}`
	}

	isPublicOrderingClosedForAnyType() {
		//can't use the method directly as the argument... otherwise `this` is lost?
		return this.enabled_base_order_types.some(type => this.isPublicOrderingClosedForType(type))
	}

	isPublicOrderingClosedForAllTypes() {
		return !this.enabled_base_order_types.some(type => !this.isPublicOrderingClosedForType(type))
	}

	/**
	 * @param type {OrderType}
	 * @return boolean
	 */
	isPublicOrderingClosedForType(type) {
		type = type.toUpperCase()
		return this.closed_until_iso[type] !== undefined && this.closed_until_iso[type]
			&& isBefore(this.newDate(), this.newDate(this.closed_until_iso[type]))
	}

	/**
	 * @param type {OrderType}
	 * @return boolean
	 */
	isClosedForeverForType(type) {
		type = type.toUpperCase()
		return this.closed_until_iso[type] !== undefined && this.closed_until_iso[type]
			&& differenceInMonths(this.newDate(this.closed_until_iso[type]), this.newDate()) > 12
	}

	/**
	 * @param type {OrderType}
	 * @returns {JSX.Element}
	 * @TODO double check if this could have some latent bug: closedForeverForType uses the raw timestamp, while the texts are also based on restaurant open/close hours. If that's a non-issue, leave a comment explaining why, to avoid further confusion. Once done, remove the comment at OrderingStatus.js:22
	 */
	ClosedTextFor(type) {
		return this.isClosedForeverForType(type)?
			() => <>{i18next.t('Indefinitely').toLowerCase()}</> : //avoids translating "Indefinitely" and "indefinitely"
			() => <Trans>until <I b zh>{{time: this.closed_until_texts[type.toUpperCase()] }}</I></Trans> //bolded by Shuyan
	}

	/**
	 * Shove a `color` parameter inside each object in the given list, respecting some rules to avoid similar colors.
	 * @see DistinguishableColors
	 * @param restaurants {object[]}
	 * @param accountId {number} Not really used for the account, just as a random seed
	 */
	static defineColors(restaurants, accountId) {
		//the "selection seed" is constant for each account, regardless of their restaurants
		const group = DistinguishableColors.selectGroup(restaurants.length, accountId)
		restaurants.forEach((restaurant, key) => {
			if (key > group.length) {
				key -= group.length //round and round we go... failsafe in case we get more restaurants than colors?
			}
			restaurant.color = group[key]
		})
	}

}
